import { create } from 'zustand'

import { login, logout, validateSession } from '@/api/apis'
import { queryClient } from '@/lib/queryClient'
import { setSession } from '@/services/jwt.service'

/**
 * Authentication state for the whole app.
 *
 * `tokenLoading` covers the cold-start check performed by <Auth>: it starts true
 * so no route renders — and no redirect fires — before a stored token has been
 * validated. `authLoading` covers an interactive sign-in.
 */
export const useAuthStore = create((set, get) => ({
  user: null,
  authLoading: false,
  tokenLoading: true,
  authError: null,

  setUser: (user) => set({ user }),

  /**
   * Ends the session, and with it every cached response.
   *
   * `queryClient` is a module singleton that outlives any one session, so
   * without this the next sign-in is served the previous user's queue, users
   * list and analytics until each entry goes stale. That is a data leak across
   * accounts, not just a refresh bug: the cache is keyed by endpoint and
   * parameters, never by who asked.
   *
   * Every way a session can end funnels through here — the sign-out button, the
   * 401 handler <Auth> registers, a failed token validation, and <Auth> finding
   * no valid token on a cold start — so this is the one place it has to happen.
   */
  logoutService: async ({ callApi = false } = {}) => {
    if (callApi) await logout().catch(() => null)
    setSession(null)
    queryClient.clear()
    set({ user: null, authLoading: false, tokenLoading: false, authError: null })
  },

  loginService: async (credentials) => {
    set({ authLoading: true, authError: null })
    try {
      const result = await login(credentials)
      const accessToken = result?.access_token ?? result?.token

      if (!result?.user || !accessToken) {
        set({ authLoading: false, user: null, authError: 'Unexpected response from the server.' })
        return false
      }

      // Belt and braces. Signing out already cleared the cache, but this makes
      // "a new session starts empty" true regardless of how this one began.
      queryClient.clear()
      setSession(accessToken)
      set({ user: result.user, authLoading: false })
      return true
    } catch (error) {
      set({ authLoading: false, user: null, authError: error.message })
      return false
    }
  },

  /** Confirms a persisted token against the API and refreshes the stored user. */
  loginWithToken: async () => {
    try {
      const result = await validateSession()
      const accessToken = result?.access_token ?? result?.token

      if (!result?.user) {
        get().logoutService()
        return
      }

      if (accessToken) setSession(accessToken)
      set({ user: result.user, tokenLoading: false })
    } catch {
      get().logoutService()
    }
  },
}))

export const selectIsAuthenticated = (state) => Boolean(state.user)
